import { Children, cloneElement, forwardRef, isValidElement, useId, type InputHTMLAttributes, type ReactElement, type ReactNode, type Ref, type TextareaHTMLAttributes } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const controlBase =
  'w-full rounded-[var(--radius-control)] border border-line-strong bg-surface px-3 text-sm text-ink ' +
  'placeholder:text-ink-subtle transition-[border-color,box-shadow] duration-150 ' +
  'focus:border-brand focus:outline-none focus:ring-4 focus:ring-[var(--color-brand-ring)] ' +
  'disabled:cursor-not-allowed disabled:bg-surface-raised disabled:text-ink-subtle';

export const Label = forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & { required?: boolean }
>(({ className, children, required, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn('text-[13px] font-medium text-ink', className)}
    {...props}
  >
    {children}
    {required && (
      <span className="ml-0.5 text-danger" aria-hidden>
        *
      </span>
    )}
  </LabelPrimitive.Root>
));
Label.displayName = 'Label';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
  /**
   * On the wrapper, so a caller can scroll the whole field — label, control and error —
   * into view rather than just the input. React 19 passes `ref` as an ordinary prop, so
   * no forwardRef is needed.
   */
  ref?: Ref<HTMLDivElement>;
}

/** Wraps a control with its label, hint and error so spacing is consistent everywhere. */
export function Field({ label, hint, error, required, htmlFor, children, className, ref }: FieldProps) {
  /**
   * The label tied to its control without every caller wiring ids by hand.
   *
   * Without `htmlFor` the label sat above the input unconnected: a screen reader
   * announced an unnamed text box, and tapping the label did not focus the field. A
   * lone control child with no id of its own is given one; anything else — several
   * children, or a control that already has an id — is left as the caller set it.
   */
  const generated = useId();
  const only = Children.count(children) === 1 && isValidElement(children) ? (children as ReactElement<{ id?: string }>) : null;
  const control = only && (typeof only.type !== 'string' || ['input', 'textarea', 'select'].includes(only.type));
  const linkable = Boolean(label && !htmlFor && control && !only?.props.id);
  const target = htmlFor ?? (linkable ? generated : undefined);
  return (
    <div ref={ref} className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <Label htmlFor={target} required={required}>
          {label}
        </Label>
      )}
      {linkable && only ? cloneElement(only, { id: generated }) : children}
      {error ? (
        <p className="flex items-center gap-1.5 text-[12.5px] text-danger" role="alert">
          <AlertCircle className="size-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      ) : (
        hint && <p className="text-[12.5px] leading-relaxed text-ink-subtle">{hint}</p>
      )}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leadingIcon?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, leadingIcon, trailing, ...props }, ref) => {
    const input = (
      <input
        ref={ref}
        className={cn(
          controlBase,
          'h-9.5',
          leadingIcon && 'pl-9',
          trailing && 'pr-10',
          invalid && 'border-danger focus:border-danger focus:ring-[color-mix(in_oklch,var(--color-danger)_30%,transparent)]',
          className
        )}
        aria-invalid={invalid || undefined}
        {...props}
      />
    );

    if (!leadingIcon && !trailing) return input;

    return (
      <div className="relative">
        {leadingIcon && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle [&_svg]:size-4">
            {leadingIcon}
          </span>
        )}
        {input}
        {trailing && <span className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</span>}
      </div>
    );
  }
);
Input.displayName = 'Input';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, rows = 4, ...props }, ref) => (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(
        controlBase,
        'resize-y py-2.5 leading-relaxed',
        invalid && 'border-danger focus:border-danger',
        className
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  )
);
Textarea.displayName = 'Textarea';

/**
 * A native select rather than a Radix one: the options here are short, flat lists and a
 * native control is better on mobile and with screen readers.
 */
export const NativeSelect = forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }
>(({ className, invalid, children, ...props }, ref) => (
  <div className="relative">
    <select
      ref={ref}
      className={cn(
        controlBase,
        'h-9.5 cursor-pointer appearance-none pr-9',
        invalid && 'border-danger',
        className
      )}
      aria-invalid={invalid || undefined}
      {...props}
    >
      {children}
    </select>
    <svg
      className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-ink-subtle"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
    >
      <path d="m6 8 4 4 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
));
NativeSelect.displayName = 'NativeSelect';

/** Generates a stable id so `Field` can wire `htmlFor` without the caller inventing one. */
export function useFieldId(prefix: string): string {
  return `${prefix}-${useId()}`;
}
